import * as React from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '../lib/utils';

import { buttonVariants } from './button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('vai-p-3', className)}
      classNames={{
        months:
          'vai-flex vai-flex-col sm:vai-flex-row vai-space-y-4 sm:vai-space-x-4 sm:vai-space-y-0',
        month: 'vai-space-y-4',
        caption:
          'vai-flex vai-justify-center vai-pt-1 vai-relative vai-items-center',
        caption_label: 'vai-text-sm vai-font-medium',
        nav: 'vai-space-x-1 vai-flex vai-items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'vai-h-7 vai-w-7 vai-bg-transparent vai-p-0 vai-opacity-50 hover:vai-opacity-100',
        ),
        nav_button_previous: 'vai-absolute vai-left-1',
        nav_button_next: 'vai-absolute vai-right-1',
        table: 'vai-w-full vai-border-collapse vai-space-y-1',
        head_row: 'vai-flex',
        head_cell:
          'vai-text-muted-foreground vai-rounded-md vai-w-9 vai-font-normal vai-text-[0.8rem]',
        row: 'vai-flex vai-w-full vai-mt-2',
        cell: 'vai-h-9 vai-w-9 vai-text-center vai-text-sm vai-p-0 vai-relative [&:has([aria-selected].day-range-end)]:vai-rounded-r-md [&:has([aria-selected].day-outside)]:vai-bg-accent/50 [&:has([aria-selected])]:vai-bg-accent first:[&:has([aria-selected])]:vai-rounded-l-md last:[&:has([aria-selected])]:vai-rounded-r-md focus-within:vai-relative focus-within:vai-z-20',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'vai-h-9 vai-w-9 vai-p-0 vai-font-normal aria-selected:vai-opacity-100',
        ),
        day_range_end: 'vai-day-range-end',
        day_selected:
          'vai-bg-primary vai-text-primary-foreground hover:vai-bg-primary hover:vai-text-primary-foreground focus:vai-bg-primary focus:vai-text-primary-foreground',
        day_today: 'vai-bg-accent vai-text-accent-foreground',
        day_outside:
          'vai-day-outside vai-text-muted-foreground aria-selected:vai-bg-accent/50 aria-selected:vai-text-muted-foreground',
        day_disabled: 'vai-text-muted-foreground vai-opacity-50',
        day_range_middle:
          'aria-selected:vai-bg-accent aria-selected:vai-text-accent-foreground',
        day_hidden: 'vai-invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft
            className={cn('vai-h-4 vai-w-4', className)}
            {...props}
          />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight
            className={cn('vai-h-4 vai-w-4', className)}
            {...props}
          />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
